import type { CaptureResult } from 'posthog-js'

import {
  templateSecretPath,
  templateSecretsInText,
} from 'utilities/secret-urls'
import CSS_WORDS from './meet-replay-css-words.json'
import {
  PHI_KINDS,
  markedFake,
  phiKindOf,
  replayFake,
  resetReplayFakes,
  stripFakeMark,
} from './meet-replay-fakes'

// A meeting URL is a join credential. Keep this guard in the SDK's outbound
// path as well as in the /meet recorder controller: a late flush or another
// tab must not send a snapshot after its own gate has closed.
let activeMeetingCode: string | null = null
// Every code this page has recorded under. A late flush, a referrer or a
// product event can still carry an earlier meeting's code after stop().
const seenMeetingCodes = new Set<string>()

/** Upper-case letters and digits only: the stored form of a meeting code. */
const normalizeCode = (code: string) =>
  code.toUpperCase().replace(/[^A-Z0-9]/g, '')

/** Sets the meeting whose code every outbound event is scrubbed of (null clears it). */
export const setMeetReplayScope = (meetingCode: string | null) => {
  const code = meetingCode ? normalizeCode(meetingCode) : ''
  // A new scope starts a new fake table and mark: nothing generated for one
  // meeting is accepted in another.
  if ((code || null) !== activeMeetingCode) resetReplayFakes()
  activeMeetingCode = code || null
  if (code) seenMeetingCodes.add(code)
}

/**
 * Whether this page has recorded a /meet call. Its late flushes can arrive
 * after the page has left /meet, so nothing off /meet is sent for the rest of
 * the page lifetime, and app-wide replay does not restart into the same
 * PostHog session.
 */
export const meetReplayUsedThisPage = (): boolean => seenMeetingCodes.size > 0

/** Tests only: forget every code this page has recorded under. */
export const resetMeetReplayScopesForTests = () => {
  activeMeetingCode = null
  seenMeetingCodes.clear()
  textParents.clear()
  styledHosts.clear()
}

// A code written in groups ("/meet/SYN THE TIC") is taken whole, separators
// and all, before the plain form that stops at the first space.
const MEET_PATH =
  /\/meet\/(?:[a-z0-9]{3}[-_.\s][a-z0-9]{3}[-_.\s][a-z0-9]{3}\b|[^/?#\s"'<>]+)/gi
const ENCODED_MEET_PATH = /%2fmeet%2f[^%?&#\s"'<>]+/gi
// Kickoff meeting codes are nine base-36 characters, shown in 3-character
// groups. On /meet, any such token is treated as a credential.
const CODE_LIKE = /\b[A-Z0-9]{3}[-_.\s]?[A-Z0-9]{3}[-_.\s]?[A-Z0-9]{3}\b/g
const REDACTED = '[redacted]'

/** A pattern for one code with any run of separators between characters, any case. */
const anyGrouping = (code: string) =>
  new RegExp(code.split('').join('[^a-z0-9]*'), 'gi')

/** Replaces meeting paths and every code this page has seen in one string. */
const scrubCredential = (value: string): string => {
  let safe = value
    .replace(MEET_PATH, '/meet/[redacted]')
    .replace(ENCODED_MEET_PATH, '%2Fmeet%2F[redacted]')
  for (const code of Array.from(seenMeetingCodes))
    safe = safe.replace(anyGrouping(code), REDACTED)
  return safe
}

/**
 * Whether text holds a code this page has seen once every separator and
 * case difference is removed (`abc,123(xyz)` counts).
 */
const carriesSeenCode = (value: string) => {
  const letters = normalizeCode(value)
  return Array.from(seenMeetingCodes).some(code => letters.includes(code))
}

/** Whether the credential scrub would change text, or it hides a seen code. */
const carriesCredential = (value: string) =>
  scrubCredential(value) !== value || carriesSeenCode(value)

/** Whether the browser is on any /meet/<code> page. */
const onMeetPage = () =>
  typeof window !== 'undefined' &&
  /^\/meet\/[^/]+\/?$/.test(window.location.pathname)

/**
 * The route a browser path reaches: percent-decoded, without the `/en`
 * locale prefix (next.config.js i18n), which serves the same page
 * (`/en/meet/<code>` is the meeting page).
 */
const routePath = (path: string | null | undefined): string => {
  let decoded = path || ''
  try {
    decoded = decodeURIComponent(decoded)
  } catch {
    // A malformed escape still gets the prefix check below.
  }
  return decoded.replace(/^\/en(?=\/|$)/i, '') || '/'
}

/**
 * Whether a path is under /meet. App-wide replay never records there; only
 * the Call Guidance controller does (components/meet/replay).
 */
export const isMeetPath = (path: string | null | undefined): boolean =>
  /^\/meet(\/|$)/i.test(routePath(path))

/** Pages that show a credential as text, by route. */
const CREDENTIAL_PAGES = /^\/auth-tokens(\/|$)/i

/**
 * Whether app-wide replay stays off a path: /meet, every route whose path is
 * itself a credential (sign-in, booking, short-code links), and every page
 * that shows one as text (/auth-tokens, where /a/<code> lands with the
 * address bar cleaned, displays the sign-in code).
 */
export const isAppReplayExcludedPath = (
  path: string | null | undefined
): boolean => {
  const route = routePath(path)
  return (
    isMeetPath(route) ||
    CREDENTIAL_PAGES.test(route) ||
    templateSecretPath(route) !== route
  )
}

/** Whether the browser is on any /meet page. */
export const onAnyMeetPage = (): boolean =>
  typeof window !== 'undefined' && isMeetPath(window.location.pathname)

// PostHog identity and SDK bookkeeping. Never meeting data, and rewriting them
// would split sessions or break ingestion.
const IDENTITY_KEYS = new Set([
  'token',
  'distinct_id',
  '$device_id',
  '$user_id',
  '$session_id',
  '$window_id',
  '$pageview_id',
  '$anon_distinct_id',
  '$lib',
  '$lib_version',
  'uuid',
])

/** Scrubs an event's properties, redacting whole values under credential-like keys. */
const scrubEventProperties = (
  value: unknown,
  codeLike: boolean,
  key = '',
  depth = 0
): unknown => {
  if (/meeting.?code|room.?sid|participant.?token/i.test(key)) return REDACTED
  if (typeof value === 'string') {
    // Only the SDK's own top-level fields are identity; a nested `token`
    // or `uuid` in a product event is ordinary data and gets scrubbed.
    // Identity skips the broad CODE_LIKE rule (an id can look like a code),
    // but never the seen-code and /meet path scrub: that leaves real ids
    // alone and still catches a code a product event put under these names.
    if (depth === 1 && IDENTITY_KEYS.has(key)) return scrubCredential(value)
    const safe = scrubCredential(value)
    return codeLike ? safe.replace(CODE_LIKE, REDACTED) : safe
  }
  if (Array.isArray(value))
    return value.map(item =>
      scrubEventProperties(item, codeLike, '', depth + 1)
    )
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value).map(([childKey, childValue]) => [
      childKey,
      scrubEventProperties(childValue, codeLike, childKey, depth + 1),
    ])
  )
}

/** The page URL reduced to the origin plus `/meet`. */
const safeMeetUrl = (): string => `${window.location.origin}/meet`

// These labels are fixed product chrome. The recorder masks every other text
// node, including custom agenda titles, client names, notes and captions.
const SAFE_LABELS = new Set([
  'Profile',
  'Agenda',
  'Notes',
  'Edit template',
  'Sections',
  'Before the Call',
  'After the Call',
  'Welcome to Kickoff',
  'Goals and History',
  'Daily Life and Circumstances',
  'The Starting Plan',
  'Next Steps and Follow-Up',
  'Connection and Early Wins',
  'Since the Last Call',
  'Practical Nutrition Guidance',
  'Obstacles and Adjustments',
  'Goals and Next Steps',
  'Check-In and Wins',
  'Goals and Progress',
  'Changes and Challenges',
  'Plan Adjustments',
])

/**
 * A /meet replay text node: a fixed product label or whitespace as it is,
 * anything else as a marked fake (clients/meet-replay-fakes) of the kind its
 * nearest `data-phi` ancestor declares. No original text is ever returned.
 */
export const maskMeetReplayText = (
  value: string,
  element?: Element | null
): string => {
  const plain = stripFakeMark(value)
  if (!plain.trim() || SAFE_LABELS.has(plain.trim())) return plain
  return replayFake(plain, phiKindOf(element))
}

/**
 * A replay text value at send time, unmarked. Only a string that still
 * carries this page's mark passes as a fake; a label or whitespace passes;
 * anything else, including unmarked text that happens to equal a fake (a
 * real "Alex Sample" or "36"), is replaced here.
 */
const replayText = (value: string): string => {
  const fake = markedFake(value)
  if (fake !== null) return fake
  const plain = stripFakeMark(value)
  if (!plain.trim() || SAFE_LABELS.has(plain.trim())) return plain
  return markedFake(replayFake(plain)) ?? ''
}

/** Whether replay text (after replayText) is a fake rather than a label. */
const isFakeText = (value: unknown) =>
  typeof value === 'string' && !!value.trim() && !SAFE_LABELS.has(value.trim())

// CSS words the app itself writes, derived from its CSS by
// scripts/replay-css-words.mjs (regenerate it when app CSS gains a word the
// replay should keep). A word outside these lists, such as a client's name
// written into a custom property, never leaves.
const PROPERTIES = new Set(CSS_WORDS.properties)
const CUSTOM_PROPERTIES = new Set(CSS_WORDS.customProperties)
const VALUE_WORDS = new Set(CSS_WORDS.valueWords)
const FUNCTIONS = new Set(CSS_WORDS.functions)
const FONTS = new Set(CSS_WORDS.fonts)
const CLASS_NAMES = new Set(CSS_WORDS.classNames)
const IDS = new Set(CSS_WORDS.ids)
const KEYFRAMES = new Set(CSS_WORDS.keyframes)
const PSEUDO = new Set(CSS_WORDS.pseudo)
const TAGS = new Set(CSS_WORDS.tags)
const ATTRIBUTE_NAMES = new Set(CSS_WORDS.attributes)
const AT_WORDS = new Set(CSS_WORDS.atWords)
// Values an id or data-* attribute (and an attribute selector) may keep:
// the words the app's CSS matches on, CSS keywords, PHI kinds and booleans.
// Anything else, a number or a free word that could be a name, is dropped.
const ATTRIBUTE_VALUES = new Set<string>([
  ...((CSS_WORDS as { attributeValues?: string[] }).attributeValues || []),
  ...CSS_WORDS.valueWords,
  ...PHI_KINDS,
  'true',
  'false',
  'on',
  'off',
])

/** Whether an attribute value is empty or a known app token. */
const isSafeToken = (value: string) =>
  value === '' ||
  (value.length <= 32 &&
    /^[a-zA-Z][\w-]*$/.test(value) &&
    ATTRIBUTE_VALUES.has(value) &&
    !carriesCredential(value))
// Hashed names from CSS modules, next/font, Emotion and its keyframes,
// accepted by shape. Keep in step with PATTERNED in the generator.
const PATTERNED_NAME =
  /^[\w-]+_[A-Za-z0-9-]+__[A-Za-z0-9_-]{5}$|^__(className|variable)_[0-9a-f]{6}$|^css-[a-z0-9]+(-[A-Za-z0-9]+)?$|^animation-[a-z0-9]+$/
const UNITS =
  /^(%|px|em|rem|ex|ch|lh|rlh|vh|vw|vmin|vmax|dvh|svh|lvh|dvw|svw|lvw|vi|vb|cqw|cqh|cqi|cqb|cm|mm|q|in|pt|pc|deg|rad|grad|turn|s|ms|hz|khz|fr|x|dpi|dpcm|dppx)?$/

// A style value is read one token at a time with a sticky pattern, so no
// input can backtrack: numbers with a CSS unit, hex colours, punctuation,
// and words, each word only from the lists above.
const VALUE_TOKEN =
  /([-+]?(?:\d+\.?\d*|\.\d+))([a-z%]*)|#[0-9a-fA-F]{3,8}(?![\w-])|(-{0,2}[a-zA-Z_][\w-]*)(\(?)|[(),/*+\s!-]/y
// Functions that load a resource or print DOM content, whatever their
// argument or vendor prefix (`-webkit-image-set(`).
const CONTENT_FUNCTION =
  /(?:url|attr|src|image|image-set|element|env|counters?)\(/i

/** Whether a value word is one the app's own CSS uses. */
const isAppWord = (word: string, call: boolean) =>
  call
    ? FUNCTIONS.has(word.toLowerCase())
    : word.startsWith('--')
    ? CUSTOM_PROPERTIES.has(word)
    : VALUE_WORDS.has(word) || KEYFRAMES.has(word) || PATTERNED_NAME.test(word)

/** Whether every character of a style value belongs to an allowed token. */
const isSafeStyleValue = (value: string) => {
  if (CONTENT_FUNCTION.test(value)) return false
  VALUE_TOKEN.lastIndex = 0
  while (VALUE_TOKEN.lastIndex < value.length) {
    const at = VALUE_TOKEN.lastIndex
    const token = VALUE_TOKEN.exec(value)
    if (!token || VALUE_TOKEN.lastIndex === at) return false
    if (token[1] !== undefined && !UNITS.test(token[2])) return false
    if (token[3] !== undefined && !isAppWord(token[3], token[4] === '('))
      return false
  }
  return true
}

/** A style property name the app uses that carries no meeting code. */
const safeStyleName = (property: string) => {
  const name = property.trim()
  const known = name.startsWith('--')
    ? CUSTOM_PROPERTIES.has(name)
    : PROPERTIES.has(name.toLowerCase())
  return known && !carriesCredential(name)
    ? name.startsWith('--')
      ? name
      : name.toLowerCase()
    : null
}

/** The form a font-family value is compared in: no quotes, no spaces at commas. */
const fontKey = (value: string) =>
  value
    .replace(/["']/g, '')
    .split(',')
    .map(part => part.trim())
    .join(',')

// Every font family the app names: each family of each shipped stack, the
// next/font families by base name (plusJakartaSans, "plusJakartaSans
// Fallback", __plusJakartaSans_34a125 ...), and the generic families.
const GENERIC_FAMILIES = [
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-sans-serif',
  'ui-serif',
  'ui-monospace',
  'ui-rounded',
  'emoji',
  'math',
  'inherit',
  'initial',
  'unset',
]
const NEXT_FONT_NAME = /^(__)?([a-zA-Z]+)(?:[_ ]Fallback)?(?:_[0-9a-f]{6})?$/
const FONT_BASES = new Set(
  CSS_WORDS.fonts.flatMap(stack =>
    stack.split(',').flatMap(family => {
      const match = /^__([a-zA-Z]+)(?:_Fallback)?_[0-9a-f]{6}$/.exec(family)
      return match ? [match[1]] : []
    })
  )
)
const FONT_FAMILIES = new Set([
  ...GENERIC_FAMILIES,
  ...CSS_WORDS.fonts.flatMap(stack =>
    stack.replace(/!important$/, '').split(',')
  ),
])

/** Whether one font family (quotes removed) is one the app names. */
const isAppFamily = (family: string) => {
  if (FONT_FAMILIES.has(family)) return true
  const next = NEXT_FONT_NAME.exec(family)
  if (next && FONT_BASES.has(next[2])) return true
  const variable = /^var\((--[\w-]+)\)$/.exec(family)
  return !!variable && CUSTOM_PROPERTIES.has(variable[1])
}

/** Whether a value is a font stack made only of app font families. */
const isFontStack = (value: string) => {
  const stack = value.replace(/\s*!important$/, '')
  if (!stack || stack.length > 300) return false
  return stack.split(',').every(part => {
    const family = part.trim()
    const quoted = /^(["'])([^"'\\]*)\1$/.exec(family)
    const name = quoted ? quoted[2] : family
    return !!name && /^[\w\s().-]+$/.test(name) && isAppFamily(name.trim())
  })
}

// `content` may only draw nothing or an icon glyph: an empty string, none
// or normal, or one or two private-use characters (Font Awesome, Quill),
// written as such or as CSS escapes. Every escape is decoded, and only a
// private-use code point (U+E000 to U+F8FF) passes, so `"\41"` ("A") is
// text and is dropped. Never text.
const CONTENT_UNIT = /\\([0-9a-fA-F]{1,6})\s?|([\s\S])/gy
const isPrivateUse = (code: number) => code >= 0xe000 && code <= 0xf8ff

/** Whether a `content` value draws nothing or one or two icon glyphs. */
const isSafeContent = (value: string) => {
  if (/^(none|normal|""|'')$/.test(value)) return true
  const quoted = /^(["'])([\s\S]*)\1$/.exec(value)
  if (!quoted) return false
  const body = quoted[2]
  let units = 0
  CONTENT_UNIT.lastIndex = 0
  while (CONTENT_UNIT.lastIndex < body.length) {
    const unit = CONTENT_UNIT.exec(body)
    if (!unit) return false
    const code = unit[1] ? parseInt(unit[1], 16) : unit[2].codePointAt(0)!
    if (!isPrivateUse(code) || ++units > 2) return false
  }
  return units > 0
}

/** One style declaration, or null when its value could carry content. */
const safeDeclaration = (property: string, value: string, maxLength = 300) => {
  const clean = value.trim()
  if (property.trim().toLowerCase() === 'content')
    return isSafeContent(clean) ? (['content', clean] as const) : null
  const name = safeStyleName(property)
  if (!name || clean.length > maxLength) return null
  // A font stack must be made of the app's own font families; a custom
  // property may hold one (--cgc-font).
  if (name === 'font-family') {
    if (!FONTS.has(fontKey(clean)) && !isFontStack(clean)) return null
  } else if (
    !isSafeStyleValue(clean) &&
    !(name.startsWith('--') && isFontStack(clean))
  )
    return null
  // A lower-case meeting code is a valid keyword, and separators can split
  // it; drop such a value rather than rewrite it.
  if (carriesCredential(clean)) return null
  return [name, clean] as const
}

/**
 * The declarations of one inline style that may leave: each must pass
 * safeDeclaration, and none may be kept when together they spell a meeting
 * credential (the whole style, or its values run together, with every
 * separator removed). `false` marks a removal in an rrweb style diff.
 */
const safeStyleEntries = (
  entries: [string, unknown][]
): [string, string | false][] => {
  const kept: [string, string | false][] = []
  for (const [property, part] of entries) {
    if (part === false) {
      const name = safeStyleName(property)
      if (name) kept.push([name, false])
      continue
    }
    const declaration =
      typeof part === 'string' ? safeDeclaration(property, part) : null
    if (declaration) kept.push([declaration[0], declaration[1]])
  }
  const set = kept.filter(([, part]) => part !== false)
  const whole = set.map(([name, part]) => `${name}:${part}`).join(';')
  const values = set.map(([, part]) => part).join('')
  return carriesCredential(whole) || carriesSeenCode(values)
    ? kept.filter(([, part]) => part === false)
    : kept
}

/** An inline style attribute with only the declarations that may leave. */
const safeInlineStyle = (value: string): string =>
  safeStyleEntries(
    value.split(';').flatMap((part): [string, string][] => {
      const colon = part.indexOf(':')
      return colon < 0 ? [] : [[part.slice(0, colon), part.slice(colon + 1)]]
    })
  )
    .map(([name, part]) => `${name}:${part}`)
    .join(';')

// Stylesheet text (the app's bundles, Emotion and Tailwind rules) goes
// through the same allowlist as inline styles: it is parsed into rules, and
// only these survive. Selectors keep tag, class, id and pseudo syntax, with
// every attribute-selector value removed; declarations must pass
// safeDeclaration; @media, @supports, @container, @layer and @keyframes
// keep their allowed contents; every other at-rule (@font-face, @import...)
// goes. A rule that hides a meeting credential or a code-shaped token is
// dropped, and an unterminated string or brace drops the whole text.

/** A CSS block: `prelude { body }`, or a `prelude;` statement (body null). */
type CssBlock = { prelude: string; body: string | null }

/** Splits CSS into top-level blocks; null for an unterminated string or brace. */
const cssBlocks = (text: string): CssBlock[] | null => {
  const blocks: CssBlock[] = []
  let start = 0
  let open = -1
  let depth = 0
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '\\') {
      i++
      continue
    }
    if (ch === '"' || ch === "'") {
      const close = text.indexOf(ch, i + 1)
      const newline = text.indexOf('\n', i + 1)
      if (close < 0 || (newline >= 0 && newline < close)) return null
      i = close
    } else if (ch === '{') {
      if (depth === 0) open = i
      depth++
    } else if (ch === '}') {
      depth--
      if (depth < 0) return null
      if (depth === 0) {
        blocks.push({
          prelude: text.slice(start, open),
          body: text.slice(open + 1, i),
        })
        start = i + 1
      }
    } else if (ch === ';' && depth === 0) {
      blocks.push({ prelude: text.slice(start, i), body: null })
      start = i + 1
    }
  }
  return depth === 0 ? blocks : null
}

// Selector tokens: names, punctuation and Tailwind's literal escapes. No
// quote, `=`, or hex escape (`\44 ana`) ever survives.
// Selector parts: a class, id, pseudo-class or element (with an opening
// bracket for its argument), an attribute name (values are already gone), a
// bare tag or keyframe step, an nth formula, and punctuation. Each name must
// be one the app's CSS uses; escapes never pass.
const SELECTOR_TOKEN =
  /\.(-?[_a-zA-Z][\w-]*)|#(-?[_a-zA-Z][\w-]*)|::?(-?[a-z][\w-]*)\(?|\[(-?[a-zA-Z_][\w-]*)(?:[~|^$*]?="([\w-]*)")?\]|(-?[a-zA-Z_][\w-]*)|[\d.]+%|\d*n(?:[+-]\d+)?|\d+|[>+~*,()\s&]/y
const SELECTOR_WORDS = new Set(['from', 'to', 'odd', 'even', 'n', 'of'])
const AT_CONDITION =
  /^@(media|supports|container|layer)(\s[\sa-z0-9():.,%/_-]*)?$/
const AT_KEYFRAMES = /^@(-webkit-)?keyframes\s+([a-zA-Z_][\w-]*)$/

/** Whether each word of an at-rule condition is one the app's CSS uses. */
const isAppCondition = (head: string) =>
  AT_CONDITION.test(head) &&
  (head.replace(/^@[a-z-]+/, '').match(/-?[a-zA-Z_][\w-]*/g) || []).every(
    word =>
      AT_WORDS.has(word) ||
      PROPERTIES.has(word) ||
      VALUE_WORDS.has(word) ||
      UNITS.test(word)
  )

/** Whether a stylesheet's keyframes name is one the app defines. */
const isAppKeyframes = (head: string) => {
  const name = AT_KEYFRAMES.exec(head)?.[2]
  return !!name && (KEYFRAMES.has(name) || PATTERNED_NAME.test(name))
}

/** Whether every part of a selector is app vocabulary. */
const isAppSelector = (selector: string) => {
  SELECTOR_TOKEN.lastIndex = 0
  while (SELECTOR_TOKEN.lastIndex < selector.length) {
    const at = SELECTOR_TOKEN.lastIndex
    const token = SELECTOR_TOKEN.exec(selector)
    if (!token || SELECTOR_TOKEN.lastIndex === at) return false
    const [, className, id, pseudo, attribute, attributeValue, bare] = token
    if (
      (className !== undefined &&
        !CLASS_NAMES.has(className) &&
        !PATTERNED_NAME.test(className)) ||
      (id !== undefined && !IDS.has(id)) ||
      (pseudo !== undefined && !PSEUDO.has(pseudo)) ||
      (attribute !== undefined && !ATTRIBUTE_NAMES.has(attribute)) ||
      (attributeValue !== undefined && !isSafeToken(attributeValue)) ||
      (bare !== undefined && !TAGS.has(bare) && !SELECTOR_WORDS.has(bare))
    )
      return false
  }
  return true
}
const CODE_SHAPED = new RegExp(CODE_LIKE.source)

const ATTRIBUTE_NAME = /^\s*(-?[a-zA-Z_][\w-]*)/
// `name op "value" [i|s]` inside an attribute selector.
const ATTRIBUTE_SELECTOR =
  /^\s*(-?[a-zA-Z_][\w-]*)\s*([~|^$*]?=)\s*(?:"([^"]*)"|'([^']*)'|([\w-]+))\s*[is]?\s*$/

/**
 * Every attribute selector reduced to its name (`[data-x="…" i]` becomes
 * `[data-x]`), in one pass; null when a bracket or string never closes.
 */
const withoutAttributeValues = (selector: string) => {
  let out = ''
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i]
    if (ch === '\\') {
      out += selector.slice(i, i + 2)
      i++
      continue
    }
    if (ch !== '[') {
      out += ch
      continue
    }
    let end = i + 1
    let quote = ''
    while (end < selector.length) {
      const inner = selector[end]
      if (quote) {
        if (inner === quote) quote = ''
        else if (inner === '\n') return null
      } else if (inner === '"' || inner === "'") quote = inner
      else if (inner === ']') break
      end++
    }
    if (end >= selector.length) return null
    const inner = selector.slice(i + 1, end)
    const name = ATTRIBUTE_NAME.exec(inner)
    if (!name) return null
    // A value that is a safe app token (`[data-state="on"]`) is kept, so the
    // replay can tell states apart; any other value is removed.
    const kept = ATTRIBUTE_SELECTOR.exec(inner)
    const value = kept ? kept[3] ?? kept[4] ?? kept[5] : undefined
    out +=
      kept && value !== undefined && isSafeToken(value)
        ? `[${kept[1]}${kept[2]}"${value}"]`
        : `[${name[1]}]`
    i = end
  }
  return out
}

/** A selector with attribute values removed, or null when anything else is odd. */
const safeSelector = (prelude: string) => {
  // Cap before any scan, so a huge prelude costs nothing. The app's longest
  // selector list is a few thousand characters.
  if (prelude.length > 10_000) return null
  const selector = withoutAttributeValues(prelude.trim())
  return selector && isAppSelector(selector) ? selector : null
}

/** Splits a declaration block on `;` outside parentheses. */
const declarationParts = (body: string) => {
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < body.length; i++) {
    if (body[i] === '(') depth++
    else if (body[i] === ')') depth = Math.max(0, depth - 1)
    else if (body[i] === ';' && depth === 0) {
      parts.push(body.slice(start, i))
      start = i + 1
    }
  }
  parts.push(body.slice(start))
  return parts
}

/** A rule's declarations, each passing safeDeclaration. */
const safeDeclarations = (body: string) =>
  declarationParts(body)
    .flatMap(part => {
      const colon = part.indexOf(':')
      if (colon < 0) return []
      const kept = safeDeclaration(
        part.slice(0, colon),
        part.slice(colon + 1),
        2000
      )
      return kept ? [`${kept[0]}:${kept[1]}`] : []
    })
    .join(';')

// @font-face may load only the app's own font files, from this page's
// origin or the production site, so the player renders real type. Any other
// url() anywhere in CSS is still dropped (CONTENT_FUNCTION).
const FONT_ORIGINS = [
  'https://www.trainwithkickoff.com',
  'https://trainwithkickoff.com',
]
const FONT_PATH =
  /^\/(?:_next\/static\/media\/[\w.-]+|fonts\/[\w-]+(?:\/[\w.-]+)*)\.(?:woff2?|ttf|otf)$/
const FONT_FORMAT = /^format\((["'])(woff2|woff|truetype|opentype)\1\)$/
const FONT_DESCRIPTOR_VALUE: Record<string, RegExp> = {
  'font-weight': /^(normal|bold|bolder|lighter|\d{3})(\s+\d{3})?$/,
  'font-style': /^(normal|italic|oblique(\s+-?\d+deg){0,2})$/,
  'font-display': /^(auto|block|swap|fallback|optional)$/,
  'font-stretch': /^(normal|\d+(\.\d+)?%(\s+\d+(\.\d+)?%)?|[a-z-]+)$/,
  'unicode-range':
    /^U\+[0-9A-Fa-f?]{1,6}(-[0-9A-Fa-f]{1,6})?(\s*,\s*U\+[0-9A-Fa-f?]{1,6}(-[0-9A-Fa-f]{1,6})?)*$/,
  'size-adjust': /^\d+(\.\d+)?%$/,
  'ascent-override': /^(normal|\d+(\.\d+)?%)$/,
  'descent-override': /^(normal|\d+(\.\d+)?%)$/,
  'line-gap-override': /^(normal|\d+(\.\d+)?%)$/,
}

/** Whether a font url() points at one of the app's own font files. */
const isAppFontUrl = (raw: string) => {
  const url = raw.trim().replace(/^(["'])(.*)\1$/, '$2')
  let path = url
  for (const origin of [
    ...FONT_ORIGINS,
    typeof window !== 'undefined' ? window.location.origin : '',
  ])
    if (origin && url.startsWith(`${origin}/`)) path = url.slice(origin.length)
  return FONT_PATH.test(path)
}

/** One `src` source: url(...) [format(...)], or local(<app family>). */
const isFontSource = (source: string) => {
  const part = source.trim()
  const local = /^local\((["']?)([\w\s-]+)\1\)$/.exec(part)
  if (local) return isAppFamily(local[2].trim())
  const url = /^url\(([^()\s]+)\)(?:\s+(format\([^()]*\)))?$/.exec(part)
  return !!url && isAppFontUrl(url[1]) && (!url[2] || FONT_FORMAT.test(url[2]))
}

/** An @font-face body with only the app's own family, files and descriptors. */
const safeFontFace = (body: string) => {
  let family = false
  let source = false
  const kept: string[] = []
  for (const part of declarationParts(body)) {
    const colon = part.indexOf(':')
    if (colon < 0) continue
    const name = part.slice(0, colon).trim().toLowerCase()
    const value = part.slice(colon + 1).trim()
    if (name === 'font-family') {
      if (!isFontStack(value) || value.includes(',')) return ''
      family = true
    } else if (name === 'src') {
      if (!value.split(',').every(isFontSource)) return ''
      source = true
    } else if (!FONT_DESCRIPTOR_VALUE[name]?.test(value)) continue
    kept.push(`${name}:${value}`)
  }
  return family && source ? kept.join(';') : ''
}

/** The allowed rules of a stylesheet text, nesting at most four deep. */
const safeRules = (text: string, depth = 0): string => {
  const blocks = depth > 4 ? null : cssBlocks(text)
  if (!blocks) return ''
  const rules: string[] = []
  for (const { prelude, body } of blocks) {
    const head = prelude.trim()
    if (body === null) continue
    let rule = ''
    if (head.toLowerCase() === '@font-face') {
      const inner = safeFontFace(body)
      if (inner) rule = `@font-face{${inner}}`
    } else if (head.startsWith('@')) {
      if (isAppCondition(head) || isAppKeyframes(head)) {
        const inner = safeRules(body, depth + 1)
        if (inner) rule = `${head}{${inner}}`
      }
    } else {
      const selector = safeSelector(head)
      const declarations = selector && safeDeclarations(body)
      if (declarations) rule = `${selector}{${declarations}}`
    }
    if (rule && !carriesCredential(rule) && !CODE_SHAPED.test(rule))
      rules.push(rule)
  }
  return rules.join('')
}

/** Stylesheet text reduced to its allowed rules ('' when nothing may leave). */
// Far above any stylesheet this app ships; anything bigger is not sent.
const MAX_CSS_LENGTH = 2_000_000

const safeCss = (value: string): string => {
  if (value.length > MAX_CSS_LENGTH) return ''
  const safe = safeRules(value.replace(/\/\*[\s\S]*?(?:\*\/|$)/g, ''))
  // Rules that spell a code only together are still a code: check the
  // whole text, its selectors alone and its values alone.
  const selectors = safe.replace(/\{[^{}]*\}/g, ' ')
  const values = (safe.match(/:[^;{}]*/g) || []).join('')
  return carriesCredential(safe) ||
    carriesSeenCode(selectors) ||
    carriesSeenCode(values)
    ? ''
    : safe
}

/**
 * Coverage check only (test/unit/meet/replay-css-coverage.test.ts): how many
 * style rules and declarations a stylesheet has, how many the replay keeps,
 * and the kept text.
 */
export const replayCssCoverage = (css: string) => {
  /** Declarations in a rule body, nested blocks left out. */
  const declarationsIn = (body: string) => {
    let flat = body
    while (/\{[^{}]*\}/.test(flat))
      flat = flat.replace(/[^{};]*\{[^{}]*\}/g, '')
    return declarationParts(flat).filter(part => part.includes(':')).length
  }
  const count = (text: string, depth = 0): Count =>
    (depth > 4 ? null : cssBlocks(text))?.reduce(
      (total, { prelude, body }) => {
        if (body === null) return total
        if (
          prelude.trim().startsWith('@') &&
          !/^@font-face$/i.test(prelude.trim())
        ) {
          const inner = count(body, depth + 1)
          return {
            rules: total.rules + inner.rules,
            declarations: total.declarations + inner.declarations,
          }
        }
        return {
          rules: total.rules + 1,
          declarations: total.declarations + declarationsIn(body),
        }
      },
      { rules: 0, declarations: 0 }
    ) ?? { rules: 0, declarations: 0 }
  const source = count(css.replace(/\/\*[\s\S]*?(?:\*\/|$)/g, ''))
  const safe = safeCss(css)
  const kept = count(safe)
  return {
    safe,
    rules: source.rules,
    kept: kept.rules,
    declarations: source.declarations,
    keptDeclarations: kept.declarations,
  }
}
type Count = { rules: number; declarations: number }

// This is a second line of defense after rrweb masking. It preserves layout
// classes, styles and interaction state, but removes DOM attributes that can
// carry profile text, room URLs, tokens or client-specific links.
const SAFE_ATTRIBUTES = new Set([
  'class',
  'role',
  'type',
  'disabled',
  'hidden',
  'aria-selected',
  'aria-expanded',
  'aria-checked',
  'aria-hidden',
  'tabindex',
])

// React's useId values (":r1:", "«r1»"): counters, never content.
const REACT_ID = /^(:r[0-9a-z]{1,8}:|«r[0-9a-z]{1,8}»)$/
const DATA_ATTRIBUTE = /^data-[a-z][a-z0-9-]{0,40}$/

// The hatched box every /meet image becomes in a replay, at its rendered
// size. Only numbers vary, so it carries nothing of the image.
const placeholderSvg = (width: number | string, height: number | string) =>
  `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}' preserveAspectRatio='none'><defs><pattern id='h' width='8' height='8' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'><rect width='4' height='8' fill='%2384cc16' fill-opacity='.35'/></pattern></defs><rect width='100%' height='100%' fill='%23e2e8f0'/><rect width='100%' height='100%' fill='url(%23h)'/></svg>`
const PLACEHOLDER_PREFIX = 'data:image/svg+xml;charset=utf-8,'

/** The replay placeholder for an image of this size (0 for unknown). */
export const imagePlaceholder = (width: number, height: number) => {
  const w = Math.max(1, Math.min(4096, Math.round(width) || 0)) || 1
  const h = Math.max(1, Math.min(4096, Math.round(height) || 0)) || 1
  return PLACEHOLDER_PREFIX + placeholderSvg(w, h)
}
const PLACEHOLDER = new RegExp(
  `^${(PLACEHOLDER_PREFIX + placeholderSvg('WIDTH', 'HEIGHT'))
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/WIDTH|HEIGHT/g, '\\d{1,4}')}$`
)

/** Whether a src is exactly the replay image placeholder. */
const isImagePlaceholder = (value: string) =>
  value.length < 2000 && PLACEHOLDER.test(value)

// SVG icon geometry and paint. Path commands, numbers, keywords and hex
// colours only, so a name or note can never ride along in an attribute.
const SVG_ATTRIBUTES = new Set([
  'viewBox',
  'd',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'fill-rule',
  'clip-rule',
  'width',
  'height',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'x2',
  'y1',
  'y2',
  'points',
  'transform',
  'opacity',
  'fill-opacity',
  'stroke-opacity',
  'preserveAspectRatio',
])
const GEOMETRY = /^[-\d\s.,eE+MmLlHhVvCcSsQqTtAaZz%()]*$/
const PAINT_KEYWORD =
  /^(none|currentColor|transparent|round|square|butt|miter|bevel|evenodd|nonzero|(xMin|xMid|xMax)(YMin|YMid|YMax)( meet| slice)?|rotate|translate|scale|matrix)$/
const HEX_COLOUR = /^#[0-9a-fA-F]{3,8}$/
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'
// rrweb keeps a blocked element's box as rr_width/rr_height ("640px").
const BLOCKED_SIZE = /^\d+(\.\d+)?px$/

/** One DOM attribute value, or undefined when it may carry content. */
const safeAttribute = (key: string, value: unknown): unknown => {
  // A mutation removes an attribute with null; removals carry no content.
  if (value === null) return null
  // rrweb sends an inline style change as a {property: value | false} diff.
  if (key === 'style' && typeof value === 'object')
    return Object.fromEntries(
      safeStyleEntries(Object.entries(value as Record<string, unknown>))
    )
  if (typeof value !== 'string')
    return SAFE_ATTRIBUTES.has(key) ? value : undefined
  if (SAFE_ATTRIBUTES.has(key)) return scrubCredential(value)
  // Ids and data-* attributes carry the layout and state the app's CSS
  // selects on (#app, [data-state="on"]), with their values limited to app
  // tokens: a value that could be content is dropped.
  if (key === 'id')
    return (IDS.has(value) || REACT_ID.test(value)) && !carriesCredential(value)
      ? value
      : undefined
  if (DATA_ATTRIBUTE.test(key)) return isSafeToken(value) ? value : undefined
  // An image source is only ever the replay placeholder (maskReplayAttribute).
  if (key === 'src') return isImagePlaceholder(value) ? value : undefined
  // An input's value is only ever a marked fake (maskReplayInput); anything
  // else is replaced.
  if (key === 'value') return replayText(value)
  if (key === 'style') return safeInlineStyle(value)
  if (key === '_cssText') return safeCss(value)
  if (key === 'rr_width' || key === 'rr_height')
    return BLOCKED_SIZE.test(value) ? value : undefined
  if (key === 'xmlns') return value === SVG_NAMESPACE ? value : undefined
  // Path syntax allows some letters (M, L, C, S, Q, A ...), so a code made of
  // only those could pass GEOMETRY: check for a seen code too.
  if (SVG_ATTRIBUTES.has(key) && value.length <= 8192)
    return (GEOMETRY.test(value) ||
      PAINT_KEYWORD.test(value) ||
      HEX_COLOUR.test(value)) &&
      !carriesCredential(value)
      ? value
      : undefined
  return undefined
}

/** Keeps only structural layout, style and state attributes on a replay DOM node. */
const cleanAttributes = (attributes: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(attributes)
      .map(([key, value]) => [key, safeAttribute(key, value)])
      .filter(([, value]) => value !== undefined)
  )

const STRUCTURAL_TEXT = new Set([
  'tagName',
  'plugin',
  'type',
  'source',
  'compatMode',
])
const STRUCTURAL_NUMBERS = new Set([
  'type',
  'source',
  'timestamp',
  'timeOffset',
  'id',
  'parentId',
  'nextId',
  'previousId',
  'rootId',
  'styleId',
  'x',
  'y',
  'width',
  'height',
  'top',
  'left',
  'scrollLeft',
  'scrollTop',
  'offset',
  'index',
  'interaction',
  'pointerType',
  'start',
  'end',
  'startOffset',
  'endOffset',
])

// rrweb incremental sources that only carry stylesheet rules:
// StyleSheetRule, StyleDeclaration and AdoptedStyleSheet.
const STYLE_SOURCES = new Set([8, 13, 15])

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/** A rule index: a number, or a path of numbers into nested rules. */
const safeIndex = (value: unknown) =>
  isNumber(value)
    ? value
    : Array.isArray(value) && value.every(isNumber)
    ? value
    : undefined

/** Inserted rules: each rule is safe CSS, or the entry is dropped. */
const safeRuleList = (value: unknown) =>
  Array.isArray(value)
    ? value.flatMap(item => {
        const rule = safeCss(typeof item?.rule === 'string' ? item.rule : '')
        const index = safeIndex(item?.index)
        return rule ? [index === undefined ? { rule } : { rule, index }] : []
      })
    : undefined

/** Only the defined object, with the keys that are present. */
const defined = (value: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined))

/**
 * A stylesheet event (rrweb StyleSheetRule, StyleDeclaration or
 * AdoptedStyleSheet) rebuilt from its known fields only. Any other key, and
 * any value of the wrong type, is dropped.
 */
const cleanStyleEvent = (data: Record<string, unknown>) => {
  const set = data.set as Record<string, unknown> | undefined
  const declaration =
    set && typeof set.property === 'string' && typeof set.value === 'string'
      ? safeDeclaration(set.property, set.value, 2000)
      : null
  const removed =
    typeof (data.remove as Record<string, unknown>)?.property === 'string'
      ? safeStyleName((data.remove as Record<string, string>).property)
      : null
  return defined({
    source: data.source,
    id: isNumber(data.id) ? data.id : undefined,
    styleId: isNumber(data.styleId) ? data.styleId : undefined,
    index: safeIndex(data.index),
    adds: safeRuleList(data.adds),
    removes: Array.isArray(data.removes)
      ? data.removes.flatMap(item => {
          const index = safeIndex(item?.index)
          return index === undefined ? [] : [{ index }]
        })
      : undefined,
    replace:
      typeof data.replace === 'string' ? safeCss(data.replace) : undefined,
    replaceSync:
      typeof data.replaceSync === 'string'
        ? safeCss(data.replaceSync)
        : undefined,
    set: declaration
      ? defined({
          property: declaration[0],
          value: declaration[1],
          priority: set?.priority === 'important' ? 'important' : undefined,
        })
      : undefined,
    remove: removed ? { property: removed } : undefined,
    styleIds:
      Array.isArray(data.styleIds) && data.styleIds.every(isNumber)
        ? data.styleIds
        : undefined,
    styles: Array.isArray(data.styles)
      ? data.styles.flatMap(item =>
          isNumber(item?.styleId)
            ? [{ styleId: item.styleId, rules: safeRuleList(item.rules) ?? [] }]
            : []
        )
      : undefined,
  })
}

/** Walks replay snapshot data, masking text and attributes outside the allowlist. */
const cleanReplayData = (value: unknown, key = ''): unknown => {
  // Array items inherit their field's name (an `index` path is number[]).
  if (Array.isArray(value)) return value.map(item => cleanReplayData(item, key))
  if (typeof value === 'number')
    return STRUCTURAL_NUMBERS.has(key) ? value : REDACTED
  if (typeof value === 'string') {
    if (STRUCTURAL_TEXT.has(key)) return scrubCredential(value)
    if (key === 'textContent' || key === 'value' || key === 'text')
      return replayText(value)
    if (key === 'href' || key === 'url') return safeMeetUrl()
    // The doctype (`<!DOCTYPE html>`): its name and empty ids only.
    if (
      (key === 'name' && value === 'html') ||
      ((key === 'publicId' || key === 'systemId') && value === '')
    )
      return value
    // A new rrweb field must never quietly carry arbitrary client content.
    return REDACTED
  }
  if (!value || typeof value !== 'object') return value
  const node = value as Record<string, unknown>
  return Object.fromEntries(
    Object.entries(node).map(([childKey, childValue]) => {
      if (
        childKey === 'attributes' &&
        childValue &&
        !Array.isArray(childValue) &&
        typeof childValue === 'object'
      )
        return [
          childKey,
          cleanAttributes(childValue as Record<string, unknown>),
        ]
      // rrweb flags a <style> element's text node; keep the CSS, not text.
      if (
        childKey === 'textContent' &&
        node.isStyle === true &&
        typeof childValue === 'string'
      )
        return [childKey, safeCss(childValue)]
      return [childKey, cleanReplayData(childValue, childKey)]
    })
  )
}

/** One rrweb event cleaned for upload. */
const cleanSnapshot = (snapshot: Record<string, unknown>) => {
  const data = snapshot.data as Record<string, unknown> | undefined
  if (snapshot.type === 3 && data && STYLE_SOURCES.has(data.source as number))
    return {
      type: snapshot.type,
      timestamp: snapshot.timestamp,
      data: cleanStyleEvent(data),
    }
  return cleanReplayData(snapshot)
}

// Replay-only marking of fakes: the player draws every element whose text
// or value is a fake with a lime hatch. The style exists only in the uploaded
// snapshot, never in the live page.
const FAKE_ATTRIBUTE = 'data-replay-fake'
export const REPLAY_FAKE_CSS =
  '[data-replay-fake]{background:repeating-linear-gradient(45deg,rgba(132,204,22,.22) 0 3px,transparent 3px 6px);filter:blur(.4px)}'
// Ids for the injected <style> nodes, far above any id rrweb assigns.
let injectedId = 2_000_000_000
// Text node id to its parent element id, so a later text change can tag
// the right element; and the shadow hosts already given the style.
const textParents = new Map<number, number>()
const styledHosts = new Set<number>()

type ReplayNode = {
  type?: number
  id?: number
  tagName?: string
  attributes?: Record<string, unknown>
  childNodes?: ReplayNode[]
  textContent?: string
  isStyle?: boolean
  isShadow?: boolean
  isShadowHost?: boolean
}

const styleNode = (shadow: boolean): ReplayNode => ({
  type: 2,
  tagName: 'style',
  attributes: { _cssText: REPLAY_FAKE_CSS },
  childNodes: [],
  id: injectedId++,
  ...(shadow ? { isShadow: true } : {}),
})

/** Whether a cleaned text node shows a fake. */
const isFakeTextNode = (node: ReplayNode) =>
  node.type === 3 && !node.isStyle && isFakeText(node.textContent)

/**
 * Marks a cleaned node tree in place: tags elements with fake text or a fake
 * value, gives every image the placeholder, records text parents, and adds
 * the fake style to a document head and each shadow root.
 */
const markTree = (node: ReplayNode, parentId?: number) => {
  if (node.type === 3 && isNumber(node.id) && isNumber(parentId))
    textParents.set(node.id, parentId)
  if (node.type !== 2 && node.type !== 0) {
    node.childNodes?.forEach(child => markTree(child, node.id))
    return
  }
  const children = node.childNodes || []
  if (node.type === 2) {
    const attributes = (node.attributes ||= {})
    const tag = node.tagName?.toLowerCase()
    if (
      children.some(isFakeTextNode) ||
      (typeof attributes.value === 'string' && isFakeText(attributes.value))
    )
      attributes[FAKE_ATTRIBUTE] = ''
    if (tag === 'img' && typeof attributes.src !== 'string')
      attributes.src = imagePlaceholder(0, 0)
    if (tag === 'head') children.push(styleNode(false))
    if (
      node.isShadowHost &&
      isNumber(node.id) &&
      children.some(child => child.isShadow)
    ) {
      styledHosts.add(node.id)
      children.push(styleNode(true))
    }
    node.childNodes = children
  }
  children.forEach(child => markTree(child, node.id))
}

type Mutation = {
  source?: number
  adds?: { parentId?: number; node?: ReplayNode }[]
  removes?: { parentId?: number; id?: number }[]
  texts?: { id?: number; value?: unknown }[]
  attributes?: { id?: number; attributes?: Record<string, unknown> }[]
}

// Text parents kept between full snapshots before the table starts over. A
// lost entry only drops the hatch from a later text change; the text itself
// is still a fake.
const MAX_TEXT_PARENTS = 20_000

/** The attribute changes that tag these elements as fakes. */
const tagsFor = (ids: Iterable<number>) =>
  Array.from(new Set(ids)).map(id => ({
    id,
    attributes: { [FAKE_ATTRIBUTE]: '' },
  }))

/**
 * Marks one cleaned rrweb event in place, returning any extra event it needs
 * (an input change tags its element through a separate mutation).
 */
const markSnapshot = (snapshot: Record<string, unknown>) => {
  const data = snapshot.data as Record<string, unknown> | undefined
  if (!data) return null
  if (snapshot.type === 2) {
    textParents.clear()
    styledHosts.clear()
    markTree(data.node as ReplayNode)
    return null
  }
  if (snapshot.type !== 3) return null
  if (data.source === 0) {
    const mutation = data as Mutation
    const tagged: number[] = []
    for (const remove of mutation.removes || [])
      if (isNumber(remove.id)) textParents.delete(remove.id)
    if (textParents.size > MAX_TEXT_PARENTS) textParents.clear()
    for (const add of mutation.adds || []) {
      if (!add.node) continue
      markTree(add.node, add.parentId)
      if (isFakeTextNode(add.node) && isNumber(add.parentId))
        tagged.push(add.parentId)
    }
    for (const add of [...(mutation.adds || [])])
      if (
        add.node?.isShadow &&
        isNumber(add.parentId) &&
        !styledHosts.has(add.parentId)
      ) {
        styledHosts.add(add.parentId)
        mutation.adds!.push({ parentId: add.parentId, node: styleNode(true) })
      }
    for (const text of mutation.texts || []) {
      const parent = isNumber(text.id) ? textParents.get(text.id) : undefined
      if (parent !== undefined && isFakeText(text.value)) tagged.push(parent)
    }
    // A value set later by an attribute change (setAttribute('value'),
    // defaultValue) is a fake after cleaning: tag its element too.
    for (const change of mutation.attributes || [])
      if (isNumber(change.id) && isFakeText(change.attributes?.value))
        tagged.push(change.id)
    if (tagged.length)
      mutation.attributes = [...(mutation.attributes || []), ...tagsFor(tagged)]
    return null
  }
  // An input change: tag the input itself.
  if (data.source === 5 && isNumber(data.id) && isFakeText(data.text))
    return {
      type: 3,
      timestamp: snapshot.timestamp,
      data: {
        source: 0,
        texts: [],
        attributes: tagsFor([data.id]),
        removes: [],
        adds: [],
      },
    }
  return null
}

/** A cleaned batch with fakes marked and any extra tagging events added. */
const markSnapshots = (snapshots: Record<string, unknown>[]) =>
  snapshots.flatMap(snapshot => {
    const extra = markSnapshot(snapshot)
    return extra ? [snapshot, extra] : [snapshot]
  })

/** Whether the browser is still on the meeting the replay scope was set for. */
const onEligibleMeetPage = () => {
  if (!activeMeetingCode || !onMeetPage()) return false
  const code = window.location.pathname.match(/^\/meet\/([^/]+)\/?$/)?.[1]
  return !!code && normalizeCode(code) === activeMeetingCode
}

/** A value with every string inside it passed through clean. */
const walkStrings = (
  value: unknown,
  clean: (text: string) => string
): unknown => {
  if (typeof value === 'string') return clean(value)
  if (Array.isArray(value)) return value.map(inner => walkStrings(inner, clean))
  if (!value || typeof value !== 'object') return value
  // The event's own `timestamp` is a Date; turning it into `{}` would make
  // PostHog drop the batch. Any other non-plain object can't be walked
  // safely, so it fails the batch closed (scrubAppSnapshot drops it).
  if (Object.prototype.toString.call(value) === '[object Date]') return value
  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null)
    throw new Error('Unscrubbable replay value')
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [
      key,
      walkStrings(inner, clean),
    ])
  )
}

/** One string without meeting paths, seen codes or secret URLs. */
const scrubText = (text: string) => scrubCredential(templateSecretsInText(text))

/**
 * Every string inside a value, with meeting paths, seen codes and every other
 * secret URL (sign-in, booking, short-code, OAuth; utilities/secret-urls)
 * removed.
 */
const scrubDeep = (value: unknown): unknown => walkStrings(value, scrubText)

// Query parameters, or values, that make a URL a bearer credential: S3,
// CloudFront, Google Cloud Storage, Azure SAS, OAuth and generic signed
// links, and JWT-shaped values.
const SIGNED_QUERY =
  /(?:^|[?&#;])(?:x-amz-[a-z-]+|x-goog-[a-z-]+|googleaccessid|signature|sig|key-pair-id|policy|expires|token|access_token|id_token|code)=|=eyJ[\w-]*\.[\w-]+\./i

/** Whether a query string or fragment signs its URL, raw or percent-decoded. */
const signsUrl = (query: string) => {
  let text = query
  // A signed link nested in another URL's query (an image proxy's url=) is
  // encoded once or twice.
  // Escapes are decoded byte by byte, so a malformed one cannot end the
  // check early.
  for (let pass = 0; pass < 3; pass++) {
    if (SIGNED_QUERY.test(text)) return true
    const decoded = text.replace(/%([0-9a-f]{2})/gi, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    if (decoded === text) return false
    text = decoded
  }
  return SIGNED_QUERY.test(text)
}

/** Text with the query string and fragment removed from every signed link. */
const unsignedUrlsInText = (text: string) =>
  text.replace(/[?#][^\s"'()<>\\`]*/g, query => (signsUrl(query) ? '' : query))

/**
 * One /meet string for the unredacted recording: also without signed links
 * (an inline background-image or a picture source holds them too), and
 * blanked when it still spells a seen code once separators are removed.
 */
const scrubMeetText = (text: string) => {
  const safe = scrubText(unsignedUrlsInText(text))
  return carriesSeenCode(safe) ? '' : safe
}

/** Every string inside a /meet snapshot through scrubMeetText. */
const scrubMeetDeep = (value: unknown): unknown =>
  walkStrings(value, scrubMeetText)

/**
 * An app-wide replay batch with every meeting path and seen meeting code
 * removed. A batch that cannot be walked is never sent.
 */
const scrubAppSnapshot = (event: CaptureResult): CaptureResult | null => {
  // A late /meet flush lands here once the page has left /meet; it never
  // goes out without the strict /meet cleaner.
  if (meetReplayUsedThisPage()) return null
  const snapshots = event.properties?.$snapshot_data
  // A compressed batch cannot be scrubbed (init sets compress_events: false).
  if (
    !Array.isArray(snapshots) ||
    snapshots.some(
      snapshot =>
        !snapshot ||
        typeof snapshot !== 'object' ||
        snapshot.cv ||
        typeof snapshot.data === 'string'
    )
  )
    return null
  try {
    return scrubDeep(event) as CaptureResult
  } catch {
    return null
  }
}

/** Whether a batch holds a compressed or unknown snapshot, which is never sent. */
const hasOpaqueSnapshot = (snapshots: unknown[]) =>
  snapshots.some(
    snapshot =>
      !snapshot ||
      typeof snapshot !== 'object' ||
      (snapshot as Record<string, unknown>).cv ||
      typeof (snapshot as Record<string, unknown>).data === 'string'
  )

/** A /meet snapshot batch rebuilt from known properties and cleaned events. */
const meetSnapshotEvent = (
  event: CaptureResult,
  snapshots: Record<string, unknown>[],
  clean: (snapshot: Record<string, unknown>) => unknown
): CaptureResult => ({
  ...event,
  properties: {
    // PostHog's capture endpoint rejects a snapshot batch without the
    // public project token or a distinct_id. Neither is meeting data.
    token: event.properties.token,
    distinct_id: event.properties.distinct_id,
    $session_id: event.properties.$session_id,
    $window_id: event.properties.$window_id,
    $snapshot_bytes: event.properties.$snapshot_bytes,
    $lib: event.properties.$lib,
    $lib_version: event.properties.$lib_version,
    $current_url: safeMeetUrl(),
    $snapshot_data: snapshots
      // Only rrweb's document, mutation, and viewport event types are
      // needed. Custom and plugin events can carry arbitrary call data.
      .filter(snapshot => [0, 1, 2, 3, 4].includes(snapshot?.type as number))
      .map(clean),
  },
})

/** PostHog's before_send hook sees the actual $snapshot event before upload. */
export const protectMeetReplayEvent = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) return null
  if (event.event !== '$snapshot') {
    // Keep existing product analytics behavior, but never attach a meeting
    // credential through PostHog's automatic URL/referrer properties or the
    // person properties ($set, $set_once) an identify carries.
    const codeLike = onMeetPage()
    const safe = { ...event }
    for (const field of ['properties', '$set', '$set_once'] as const)
      if (safe[field])
        safe[field] = scrubEventProperties(
          safe[field],
          codeLike
        ) as CaptureResult['properties']
    return safe
  }
  // App-wide replay (hooks/use-app-replay): every page but /meet. Meeting
  // links on those pages (the dashboard's Join buttons) still carry a join
  // credential, so it is scrubbed from the whole batch.
  if (!onAnyMeetPage()) return scrubAppSnapshot(event)
  if (!onEligibleMeetPage()) return null
  const snapshots = event.properties.$snapshot_data
  if (!Array.isArray(snapshots)) return null
  try {
    // PostHog can gzip full snapshots/mutation arrays before before_send.
    // With compression disabled at init, reject any compressed event if a
    // remote setting or SDK update changes that ordering.
    if (hasOpaqueSnapshot(snapshots)) return null
    const clean = meetSnapshotEvent(event, snapshots, cleanSnapshot)
    clean.properties.$snapshot_data = markSnapshots(
      clean.properties.$snapshot_data
    )
    return clean
  } catch {
    // An unknown recorder payload shape is never sent unfiltered.
    return null
  }
}

/**
 * The deployed before_send hook (clients/posthog). Off /meet it is
 * protectMeetReplayEvent. On a /meet call the recording is unredacted, under
 * the PostHog BAA: the DOM, attribute and CSS allowlists are replaced by the
 * credential scrub alone. Meeting paths, every seen meeting code, secret
 * URLs and signed links' query strings are still removed from every string
 * (one that still spells a seen code is blanked), the URL is still reduced
 * to /meet, and only an eligible meeting's page sends.
 */
export const protectMeetReplayEventSecretsOnly = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event || event.event !== '$snapshot' || !onAnyMeetPage())
    return protectMeetReplayEvent(event)
  if (!onEligibleMeetPage()) return null
  const snapshots = event.properties.$snapshot_data
  if (!Array.isArray(snapshots) || hasOpaqueSnapshot(snapshots)) return null
  try {
    return meetSnapshotEvent(event, snapshots, scrubMeetDeep)
  } catch {
    return null
  }
}

/**
 * No network or media URL is needed for the pilot replay. Elsewhere a
 * request keeps its URL with any meeting path removed.
 */
export const maskMeetReplayNetwork = (request: { name?: string }) => {
  if (!onAnyMeetPage())
    return request.name
      ? {
          ...request,
          name: scrubCredential(templateSecretsInText(request.name)),
        }
      : request
  return Object.keys(request).length === 1 && request.name
    ? { name: safeMeetUrl() }
    : null
}

/**
 * Replay text: kept as is everywhere. On /meet the send-time credential
 * scrub (protectMeetReplayEventSecretsOnly) still removes meeting codes.
 */
export const maskReplayText = (value: string): string => value

// Autocomplete tokens that mark a field as a secret: one-time codes,
// passwords and payment card fields.
const SECRET_AUTOCOMPLETE =
  /(^|\s)(one-time-code|current-password|new-password|cc-[a-z-]+)(\s|$)/i

/**
 * Whether an input holds a secret whatever its type: a secret autocomplete
 * token, or the input (or a wrapper) is marked `data-replay-secret`.
 */
const secretInput = (element?: Element | null): boolean => {
  if (!element) return false
  const autocomplete =
    element.getAttribute?.('autocomplete') ||
    (element as HTMLInputElement).autocomplete ||
    ''
  return (
    SECRET_AUTOCOMPLETE.test(autocomplete) ||
    !!element.closest?.('[data-replay-secret]')
  )
}

/**
 * Replay input values (maskAllInputs). On /meet the real value, unredacted
 * under the PostHog BAA; a password, hidden or secret input (secretInput) is
 * always asterisks. Off /meet it is rrweb's default asterisks, as before.
 */
export const maskReplayInput = (
  value: string,
  element?: Element | null
): string => {
  const type = (element as HTMLInputElement | null)?.type?.toLowerCase()
  if (
    !value ||
    !onAnyMeetPage() ||
    type === 'password' ||
    type === 'hidden' ||
    secretInput(element)
  )
    return '*'.repeat(value.length)
  return value
}

/**
 * An image URL with no query string or fragment, whatever the host: a query
 * can sign the link (S3, Google Cloud Storage, CloudFront, a JWT), so none
 * is recorded. data: and blob: URLs are unchanged.
 */
const bareImageUrl = (url: string) =>
  /^(data|blob):/i.test(url) ? url : url.replace(/[?#][\s\S]*$/, '')

// A srcset width, density or height descriptor ("640w", "1.5x").
const SRCSET_DESCRIPTOR = /^\d*\.?\d+[wxh]$/i

/**
 * A srcset with every candidate's URL through bareImageUrl, parsed as the
 * HTML spec does: a URL runs to the next whitespace, so a comma inside a
 * query belongs to that URL (only trailing commas end it), and descriptors
 * run to the next comma. A descriptor that is not a width, density or height
 * is dropped, so no text after a URL is kept unchecked.
 */
const bareSrcset = (value: string) => {
  const candidates: string[] = []
  let rest = value
  for (;;) {
    rest = rest.replace(/^[\s,]+/, '')
    if (!rest) break
    const url = rest.match(/^\S+/)?.[0] || ''
    rest = rest.slice(url.length)
    const link = url.replace(/,+$/, '')
    let descriptors: string[] = []
    if (link === url) {
      const end = rest.indexOf(',')
      descriptors = (end < 0 ? rest : rest.slice(0, end))
        .split(/\s+/)
        .filter(part => SRCSET_DESCRIPTOR.test(part))
      rest = end < 0 ? '' : rest.slice(end + 1)
    }
    candidates.push([bareImageUrl(link), ...descriptors].join(' '))
  }
  return candidates.join(', ')
}

/**
 * Replay attributes: unchanged here, images included, and filtered at send
 * time (secret URLs, signed links and meeting paths are scrubbed from every
 * string). On /meet an image or picture source keeps its URL without its
 * query string or fragment, so a recording never holds a working signed
 * link.
 */
export const maskReplayAttribute = (
  name: string,
  value: string,
  element?: Element | null
): string => {
  if (!onAnyMeetPage() || !['IMG', 'SOURCE'].includes(element?.tagName || ''))
    return value
  if (name === 'src') return bareImageUrl(value)
  if (name === 'srcset') return bareSrcset(value)
  return value
}
